
import { memo } from 'react';
import Layout from '@/components/v2/organisms/landing/layout/Layout';
import OptimizedPropertyCard from '@/components/OptimizedPropertyCard';
import { useProperties } from '@/hooks/useProperties';
import { useLanguage } from '@/contexts/LanguageContext';
import { PROPERTIES_KEYS } from '@/constants/translations';
import SEOHead from '@/components/SEOHead';

const Properties = memo(() => {
  const { t } = useLanguage();
  const { data: properties = [], isLoading, error } = useProperties();

  if (error) {
    return (
      <Layout>
        <SEOHead 
          title="Estancia Corta | Alojamientos en Manzanillo"
          description="Tenemos el alojamiento ideal para tus vacaciones o tú viaje de trabajo. ¡Reserva directo pagando menos!"
        />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Error al cargar propiedades</h1>
            <p className="text-gray-600">Por favor, intenta recargar la página.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead 
        title="Estancia Corta | Alojamientos en Manzanillo"
        description="Tenemos el alojamiento ideal para tus vacaciones o tú viaje de trabajo. ¡Reserva directo pagando menos!"
      />
      {/* Header */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {t(PROPERTIES_KEYS.TITLE)}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              {t(PROPERTIES_KEYS.SUBTITLE)}
            </p>
          </div>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="bg-gray-200 rounded-2xl h-96 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.map((property) => (
                <OptimizedPropertyCard 
                  key={property.id} 
                  id={property.id}
                  title={property.title}
                  shortDescription={property.shortDescription}
                  price={property.price}
                  rating={property.rating}
                  guests={property.guests}
                  images={property.images}
                  neighborhood={property.neighborhood}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
});

Properties.displayName = 'Properties';

export default Properties;
